import React from "react";
import { useNavigate } from "react-router-dom";
import { mediaSrc } from "../api";
import { useApp } from "../context/AppContext";
import { Badge } from "./ui/badge";
import { UsersRound, User, ArrowUpRight, Mic, FileText, AlertTriangle, Play } from "lucide-react";

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return "";
  }
}

/**
 * Watermark-style mirror of a job-group message inside a DM.
 * Faded + dashed, quote-style header (like a reply) showing the source group.
 * Tapping jumps straight to the original message in the group conversation.
 */
export function MirroredBubble({ message, sender, own }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const src = message.mirror_source || {};
  const media = message.media || [];

  const goToSource = () => {
    if (!src.conversation_id) return;
    navigate(`/chat/${src.conversation_id}?msg=${message.id}`);
  };

  const contentText =
    message.type === "issue"
      ? message.meta?.title || message.content
      : message.type === "voice"
        ? message.meta?.transcript || t("voice_message")
        : message.content;

  return (
    <div className={`flex mb-2 ${own ? "justify-end" : "justify-start"}`}>
      <div
        data-testid="mirrored-bubble"
        onClick={goToSource}
        className="max-w-[78%] sm:max-w-[65%] opacity-60 hover:opacity-95 transition-opacity cursor-pointer rounded-2xl border border-dashed bg-muted/40 px-3 py-2"
      >
        {/* Quote-style source header (like a reply) */}
        <div className="flex items-center gap-1.5 border-l-2 border-primary/50 pl-2 mb-1.5" data-testid="mirrored-source-header">
          {src.is_dm ? (
            <User className="h-3 w-3 text-primary shrink-0" />
          ) : (
            <UsersRound className="h-3 w-3 text-primary shrink-0" />
          )}
          <span className="text-[10.5px] font-semibold text-primary truncate">{src.name}</span>
          {src.job_number && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 font-mono-plex shrink-0">#{src.job_number}</Badge>
          )}
          <ArrowUpRight className="h-3 w-3 text-muted-foreground shrink-0 ml-auto" />
        </div>

        {sender && <div className="text-[10.5px] font-semibold text-muted-foreground mb-0.5">{sender.name}</div>}

        {/* Light content preview */}
        {message.type === "issue" && (
          <div className="flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--issue-open))] mb-0.5">
            <AlertTriangle className="h-3.5 w-3.5" /> {t("issue")}
          </div>
        )}
        {message.type === "voice" && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-0.5">
            <Mic className="h-3.5 w-3.5" /> {t("voice_message")}
          </div>
        )}
        {message.type === "document" && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-0.5">
            <FileText className="h-3.5 w-3.5" /> {message.content}
          </div>
        )}

        {media.length > 0 && (
          <div className="flex gap-1 mb-1 flex-wrap">
            {media.slice(0, 4).map((m) =>
              m.kind === "video" ? (
                <div key={m.id} className="relative h-16 w-16 rounded-md overflow-hidden bg-black shrink-0">
                  <video src={mediaSrc(m)} preload="metadata" muted playsInline className="h-full w-full object-cover pointer-events-none" />
                  <Play className="h-4 w-4 text-white absolute inset-0 m-auto" />
                </div>
              ) : m.kind === "image" ? (
                <img key={m.id} src={mediaSrc(m)} alt="" loading="lazy" className="h-16 w-16 rounded-md object-cover shrink-0" />
              ) : null
            )}
            {media.length > 4 && (
              <div className="h-16 w-16 rounded-md bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground">
                +{media.length - 4}
              </div>
            )}
          </div>
        )}

        {contentText && message.type !== "document" && (
          <p className="msg-text-sub whitespace-pre-wrap break-words">{contentText}</p>
        )}

        <div className="text-[10px] text-muted-foreground mt-1 text-right">{fmtTime(message.created_at)}</div>
      </div>
    </div>
  );
}
